import React, { useState } from 'react';
import { X, Search, Book, ChevronDown, ChevronRight } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';
import { HELP_CONTENT } from '../../data/help-content';

// Topic categories for the menu
const TOPIC_CATEGORIES = [
  {
    name: 'Clinical',
    topics: [
      { id: 'dashboard-doctor', label: 'Doctor Dashboard' },
      { id: 'consultation-workspace', label: 'Consultation & Prescriptions' },
      { id: 'referrals', label: 'Referrals' },
      { id: 'vitals-recording', label: 'Vitals Recording' },
      { id: 'discharge-summary', label: 'Discharge Summary' },
    ]
  },
  {
    name: 'Nursing',
    topics: [
      { id: 'dashboard-nurse', label: 'Nursing Dashboard' },
      { id: 'bed-management', label: 'Bed Management' },
      { id: 'medication-schedule', label: 'Medication Schedule' },
    ]
  },
  {
    name: 'Patients',
    topics: [
      { id: 'patient-registration', label: 'Registration' },
      { id: 'patient-records', label: 'Patient Records' },
      { id: 'patient-full-view', label: 'Patient Full View' },
      { id: 'patient-self-register', label: 'Self Registration' },
      { id: 'family-cards', label: 'Family Cards' },
      { id: 'emergency-registration', label: 'Emergency Registration' },
    ]
  },
  {
    name: 'Pharmacy & Lab',
    topics: [
      { id: 'pharmacy-dashboard', label: 'Pharmacy Dashboard' },
      { id: 'pharmacy-inventory', label: 'Inventory Management' },
      { id: 'pharmacy-scanning', label: 'Barcode Scanning' },
      { id: 'laboratory-dashboard', label: 'Laboratory' },
      { id: 'laboratory-results', label: 'Lab Results' },
      { id: 'imaging-requests', label: 'Imaging Requests' },
    ]
  },
  {
    name: 'Accounts & Billing',
    topics: [
      { id: 'accounts-dashboard', label: 'Billing Dashboard' },
      { id: 'service-management', label: 'Service Management' },
      { id: 'billing-invoice', label: 'Invoices' },
      { id: 'insurance-claims', label: 'Insurance Claims' },
    ]
  },
  {
    name: 'Administration',
    topics: [
      { id: 'admin-dashboard', label: 'Admin Dashboard' },
      { id: 'user-administration', label: 'User Management' },
      { id: 'facility-management', label: 'Facility Management' },
      { id: 'hospital-configuration', label: 'Hospital Configuration' },
      { id: 'duty-roster', label: 'Duty Roster' },
      { id: 'pulse-dashboard', label: 'Analytics (Pulse)' },
      { id: 'reports-analytics', label: 'Reports' },
    ]
  },
  {
    name: 'General',
    topics: [
      { id: 'general-introduction', label: 'Getting Started' },
      { id: 'user-profile', label: 'Your Profile' },
      { id: 'login', label: 'Login' },
      { id: 'accept-invite', label: 'Accept Invite' },
      { id: 'authentication-portal', label: 'Authentication' },
      { id: 'appointment-scheduling', label: 'Appointments' },
      { id: 'notifications', label: 'Notifications' },
    ]
  },
];

const HelpDrawer = () => {
  const { isOpen, closeHelp, currentTopicId, setCurrentTopicId } = useHelp();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  if (!isOpen) return null;

  const currentContent = HELP_CONTENT[currentTopicId] || HELP_CONTENT['general-introduction'];

  // Search logic
  const searchResults = searchQuery
    ? Object.entries(HELP_CONTENT).filter(([id, data]) => {
        const query = searchQuery.toLowerCase();
        return (
          data.title.toLowerCase().includes(query) ||
          data.keywords.some(k => k.toLowerCase().includes(query))
        );
      })
    : [];

  const handleTopicClick = (topicId) => {
    setCurrentTopicId(topicId);
    setSearchQuery('');
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };
  
  // Simple markdown-ish parser
  const renderContent = (content) => {
    const formatText = (text) => {
      const parts = text.split(/(\*\*.*?\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="text-foreground font-medium">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    return content.split('\n').map((line, index) => {
      if (line.startsWith('# ')) return <h1 key={index} className="text-2xl font-bold mt-4 mb-2">{line.replace('# ', '')}</h1>;
      if (line.startsWith('## ')) return <h2 key={index} className="text-xl font-semibold mt-6 mb-3 text-primary">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={index} className="text-lg font-medium mt-4 mb-2">{line.replace('### ', '')}</h3>;
      if (line.trim().startsWith('- ')) return <li key={index} className="ml-4 list-disc mb-1">{formatText(line.replace('- ', ''))}</li>;
      if (line.match(/^\d+\. /)) return <li key={index} className="ml-4 list-decimal mb-1">{formatText(line.replace(/^\d+\. /, ''))}</li>;
       
      if (line.trim() === '') return <br key={index} />;
      
      return (
        <p key={index} className="mb-2 leading-relaxed text-muted-foreground">
          {formatText(line)}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full md:w-[420px] bg-slate-100 dark:bg-slate-900 border-l-4 border-slate-500 dark:border-slate-400 shadow-[-8px_0_30px_-5px_rgba(0,0,0,0.3)] dark:shadow-[-8px_0_30px_-5px_rgba(0,0,0,0.5)] transform transition-transform duration-300 ease-in-out flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-300 dark:border-slate-700 flex items-center justify-between bg-slate-200 dark:bg-slate-800">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Book className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          Knowledge Base
        </h2>
        <button
          onClick={closeHelp}
          className="p-2 hover:bg-slate-300 dark:hover:bg-slate-700 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-slate-300 dark:border-slate-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {searchQuery ? (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Search Results</h3>
            {searchResults.length > 0 ? (
              searchResults.map(([id, data]) => (
                <button
                  key={id}
                  onClick={() => handleTopicClick(id)}
                  className="w-full text-left p-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors group"
                >
                  <div className="font-medium group-hover:text-primary transition-colors">{data.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                     {data.content.slice(0, 100).replace(/#|[*]/g, '')}...
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No topics found matching "{searchQuery}"
              </div>
            )}
            
            {searchResults.length === 0 && (
                <button 
                    onClick={() => handleTopicClick('general-introduction')}
                    className="w-full mt-4 text-center text-primary text-sm hover:underline"
                >
                    Go to General Introduction
                </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Browse Topics Menu */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Book className="w-4 h-4" />
                Browse Topics
              </h4>
              
              <div className="space-y-1">
                {TOPIC_CATEGORIES.map((category) => (
                  <div key={category.name} className="rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category.name)}
                      className="w-full flex items-center justify-between p-3 text-left font-medium text-sm bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors rounded-lg"
                    >
                      <span>{category.name}</span>
                      {expandedCategories[category.name] ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    
                    {expandedCategories[category.name] && (
                      <div className="pl-3 py-2 space-y-1 bg-slate-150 dark:bg-slate-850 rounded-b-lg">
                        {category.topics.map((topic) => (
                          <button
                            key={topic.id}
                            onClick={() => handleTopicClick(topic.id)}
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                              currentTopicId === topic.id
                                ? 'bg-slate-300 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-medium'
                                : 'hover:bg-slate-200 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {topic.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-slate-300 dark:border-slate-700 pt-4">
              <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Current Topic</h4>
            </div>
            
            {/* Current Topic Content */}
            <div className="animate-in fade-in duration-300 bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-300 dark:border-slate-700">
              {renderContent(currentContent.content)}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-300 dark:border-slate-700 bg-slate-200 dark:bg-slate-800 text-center text-xs text-muted-foreground">
        CommunityCare Help v1.0
      </div>
    </div>
  );
};

export default HelpDrawer;

